import {
  Calendar as CalendarIcon,
  Clock,
  MapPin,
  Users,
  ExternalLink,
} from "lucide-react";
import React from "react";

import { highlightSearchTerms } from "../../../../../config/search/search-utils";

import type { ProcessedEvent } from "@/types/calendar";
import { linkifyText } from "@/utils/text.utils";

interface SearchEventCardProps {
  event: ProcessedEvent;
  onClick: () => void;
  isSelected?: boolean;
  showFullDetails?: boolean;
  searchTerm?: string; // Add search term for highlighting
}

export const SearchEventCard: React.FC<SearchEventCardProps> = ({
  event,
  onClick,
  isSelected = false,
  showFullDetails = false,
  searchTerm = "",
}) => {
  const formatDate = (date: Date) => {
    return date.toLocaleDateString("en-US", {
      weekday: "short",
      month: "short",
      day: "numeric",
    });
  };

  const formatTime = (date: Date) => {
    return date.toLocaleTimeString("en-US", {
      hour: "numeric",
      minute: "2-digit",
      hour12: true,
    });
  };

  const isToday = (date: Date) => {
    const today = new Date();
    return date.toDateString() === today.toDateString();
  };

  const isTomorrow = (date: Date) => {
    const tomorrow = new Date();
    tomorrow.setDate(tomorrow.getDate() + 1);
    return date.toDateString() === tomorrow.toDateString();
  };

  const getDateLabel = (date: Date) => {
    if (isToday(date)) return "Today";
    if (isTomorrow(date)) return "Tomorrow";
    return formatDate(date);
  };

  const truncateText = (text: string, maxLength = 80) => {
    if (text.length <= maxLength) return text;
    return text.slice(0, maxLength).trim() + "...";
  };

  const handleKeyPress = (e: React.KeyboardEvent) => {
    if (e.key === "Enter" || e.key === " ") {
      e.preventDefault();
      onClick();
    }
  };

  return (
    <div
      onClick={onClick}
      onKeyDown={handleKeyPress}
      className={`
        group cursor-pointer rounded-lg border transition-all duration-200 hover:shadow-md
        ${
          isSelected
            ? "border-indigo-600 bg-gradient-to-br from-indigo-50 to-purple-50 shadow-md ring-1 ring-indigo-600/20"
            : "border-gray-200 bg-white hover:border-indigo-400 hover:bg-gradient-to-br hover:from-gray-50 hover:to-indigo-50/30"
        }
      `}
    >
      <div className="p-4" role="button" tabIndex={0}>
        {/* Header with date and time */}
        <div className="flex items-start justify-between mb-3">
          <div className="flex items-center gap-2 text-sm text-gray-600">
            <CalendarIcon className="h-4 w-4 shrink-0" />
            <span className="font-medium">{getDateLabel(event.start)}</span>
            {isToday(event.start) && (
              <span className="inline-flex items-center px-2 py-0.5 rounded-full text-xs font-medium bg-gradient-to-r from-indigo-600 to-purple-600 text-white shadow-sm">
                Today
              </span>
            )}
          </div>
          <div className="flex items-center gap-1 text-xs text-gray-500">
            <Clock className="h-3 w-3" />
            <span>{formatTime(event.start)}</span>
          </div>
        </div>

        {/* Event title */}
        <h3
          className={`
          font-semibold leading-snug mb-2 transition-colors
          ${isSelected ? "text-indigo-700" : "text-gray-900 group-hover:text-indigo-600"}
          ${showFullDetails ? "text-lg" : "text-base"}
        `}
        >
          {searchTerm
            ? highlightSearchTerms(event.title, searchTerm)
            : event.title}
        </h3>

        {/* Event description */}
        {event.description && (
          <div className="text-sm text-gray-600 mb-3 leading-relaxed">
            {showFullDetails
              ? searchTerm
                ? highlightSearchTerms(event.description, searchTerm)
                : linkifyText(event.description)
              : searchTerm
                ? highlightSearchTerms(
                    truncateText(event.description),
                    searchTerm,
                  )
                : linkifyText(truncateText(event.description))}
          </div>
        )}

        {/* Event details */}
        <div className="space-y-2">
          {/* Time range */}
          <div className="flex items-center gap-2 text-sm text-gray-500">
            <Clock className="h-4 w-4 shrink-0" />
            <span>
              {formatTime(event.start)} - {formatTime(event.end)}
              {/* Duration calculation */}
              <span className="text-gray-400 ml-1">
                (
                {Math.round(
                  (event.end.getTime() - event.start.getTime()) / (1000 * 60),
                )}
                m)
              </span>
            </span>
          </div>

          {/* Location */}
          {event.location && (
            <div className="flex items-center gap-2 text-sm text-gray-500">
              <MapPin className="h-4 w-4 shrink-0" />
              <span className="truncate">
                {searchTerm
                  ? highlightSearchTerms(event.location, searchTerm)
                  : event.location}
              </span>
            </div>
          )}

          {/* Attendees count */}
          {event.attendees && event.attendees.length > 0 && (
            <div className="flex items-center gap-2 text-sm text-gray-500">
              <Users className="h-4 w-4 shrink-0" />
              <span>
                {event.attendees.length} attendee
                {event.attendees.length !== 1 ? "s" : ""}
              </span>
            </div>
          )}

          {/* Meeting link indicator */}
          {event.meetingLink && (
            <div className="flex items-center gap-2 text-sm text-gray-500">
              <ExternalLink className="h-4 w-4 shrink-0" />
              <span>Has meeting link</span>
            </div>
          )}
        </div>

        {/* Action indicators */}
        <div className="flex items-center justify-between mt-4">
          {/* Event type/category indicator */}
          <div className="flex items-center gap-2">
            {event.meetingLink && (
              <span className="inline-flex items-center px-2 py-1 rounded-full text-xs font-medium bg-blue-100 text-blue-800">
                Virtual
              </span>
            )}
            {event.location && !event.meetingLink && (
              <span className="inline-flex items-center px-2 py-1 rounded-full text-xs font-medium bg-green-100 text-green-800">
                In-person
              </span>
            )}
          </div>

          {/* View details arrow */}
          <div
            className={`
            transition-transform duration-200 group-hover:translate-x-1
            ${isSelected ? "text-indigo-600" : "text-gray-400"}
          `}
          >
            <svg
              className="w-4 h-4"
              fill="none"
              stroke="currentColor"
              viewBox="0 0 24 24"
            >
              <path
                strokeLinecap="round"
                strokeLinejoin="round"
                strokeWidth={2}
                d="M9 5l7 7-7 7"
              />
            </svg>
          </div>
        </div>

        {/* Extended details for full view */}
        {showFullDetails && (
          <div className="mt-4 pt-4 border-t border-gray-100">
            {/* Meeting details */}
            {(event.meetingId || event.meetingPassword) && (
              <div className="space-y-1 text-sm">
                {event.meetingId && (
                  <div className="flex items-center justify-between">
                    <span className="text-gray-600">Meeting ID:</span>
                    <code className="px-2 py-1 bg-gray-100 rounded text-xs font-mono">
                      {event.meetingId}
                    </code>
                  </div>
                )}
                {event.meetingPassword && (
                  <div className="flex items-center justify-between">
                    <span className="text-gray-600">Password:</span>
                    <code className="px-2 py-1 bg-gray-100 rounded text-xs font-mono">
                      {event.meetingPassword}
                    </code>
                  </div>
                )}
              </div>
            )}

            {/* Attendees list (first few) */}
            {event.attendees && event.attendees.length > 0 && (
              <div className="mt-3">
                <div className="text-sm font-medium text-gray-700 mb-2">
                  Attendees ({event.attendees.length})
                </div>
                <div className="space-y-1">
                  {event.attendees.slice(0, 3).map((attendee, index) => (
                    <div
                      key={index}
                      className="flex items-center justify-between text-xs"
                    >
                      <span className="text-gray-600 truncate">
                        {attendee.displayName}
                      </span>
                      <span
                        className={`
                        px-2 py-0.5 rounded-full text-xs
                        ${
                          attendee.responseStatus === "accepted"
                            ? "bg-green-100 text-green-800"
                            : attendee.responseStatus === "declined"
                              ? "bg-red-100 text-red-800"
                              : attendee.responseStatus === "tentative"
                                ? "bg-yellow-100 text-yellow-800"
                                : "bg-gray-100 text-gray-800"
                        }
                      `}
                      >
                        {attendee.responseStatus === "accepted"
                          ? "Going"
                          : attendee.responseStatus === "declined"
                            ? "Not Going"
                            : attendee.responseStatus === "tentative"
                              ? "Maybe"
                              : "No Response"}
                      </span>
                    </div>
                  ))}
                  {event.attendees.length > 3 && (
                    <div className="text-xs text-gray-500 text-center">
                      +{event.attendees.length - 3} more
                    </div>
                  )}
                </div>
              </div>
            )}
          </div>
        )}
      </div>
    </div>
  );
};

export default SearchEventCard;
